import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Camera as CameraIcon, Info, MapPin, Monitor, Pencil, Plus, Radio, Router, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { Camera, Court } from "@/types";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, Badge } from "@/components/ui/Card";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { CopyableField } from "@/components/ui/CopyableField";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { LiveVideoPlayer } from "@/components/LiveVideoPlayer";
import { DeviceListEditor } from "@/components/DeviceListEditor";

// La pista con su club embebido, tal y como lo devuelve GET /courts/:id (ver backend/src/routes/courts.ts).
type CourtWithClub = Court & { club: { id: number; nombre: string } };

const cameraSchema = z
  .object({
    name: z.string().min(1, "Nombre obligatorio"),
    sourceMode: z.enum(["PULL", "PUSH"]),
    rtspUrl: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.sourceMode === "PULL" && !data.rtspUrl) {
      ctx.addIssue({ code: "custom", path: ["rtspUrl"], message: "URL RTSP obligatoria" });
    }
  });
type CameraForm = z.infer<typeof cameraSchema>;

// Las siguientes piezas (CourtInfoForm, ScoreboardConfigForm, CameraManager) son exactamente las
// que antes vivían dentro del acordeón de cada pista en ClubDetailPage — se han movido tal cual a
// esta página de ficha propia, sin reescribirlas.

function CourtInfoForm({ court }: { court: Court }) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState({
    deporte: court.deporte,
    precioBase: court.precioBase?.toString() ?? "",
    tieneLuz: court.tieneLuz,
    automatizada: court.automatizada,
  });

  const save = useMutation({
    mutationFn: () =>
      api.put(`/courts/${court.id}`, {
        deporte: values.deporte,
        precioBase: values.precioBase ? Number(values.precioBase) : null,
        tieneLuz: values.tieneLuz,
        automatizada: values.automatizada,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["courts", court.clubId] });
      toast.success("Ficha de la pista actualizada");
    },
    onError: () => toast.error("No se pudo guardar la ficha de la pista"),
  });

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-ink-100 bg-white p-4">
      <p className="flex items-center gap-2 text-sm font-medium text-ink-600">
        <Info className="h-4 w-4 text-brand-600" />
        Ficha de la pista (lo que ve el cliente al reservar)
      </p>
      <div className="grid grid-cols-2 gap-3">
        <Input
          id={`court-deporte-${court.id}`}
          label="Deporte"
          value={values.deporte}
          onChange={(e) => setValues((v) => ({ ...v, deporte: e.target.value }))}
        />
        <Input
          id={`court-precio-${court.id}`}
          label="Precio base (€)"
          type="number"
          min="0"
          step="0.01"
          value={values.precioBase}
          onChange={(e) => setValues((v) => ({ ...v, precioBase: e.target.value }))}
        />
      </div>
      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-sm text-ink-600">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-ink-200 text-brand-600 focus:ring-brand-500/30"
            checked={values.tieneLuz}
            onChange={(e) => setValues((v) => ({ ...v, tieneLuz: e.target.checked }))}
          />
          Con iluminación
        </label>
        <label className="flex items-center gap-2 text-sm text-ink-600">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-ink-200 text-brand-600 focus:ring-brand-500/30"
            checked={values.automatizada}
            onChange={(e) => setValues((v) => ({ ...v, automatizada: e.target.checked }))}
          />
          Pista automatizada
        </label>
      </div>
      <div className="flex justify-end">
        <Button type="button" onClick={() => save.mutate()} disabled={save.isPending}>
          Guardar ficha
        </Button>
      </div>
    </div>
  );
}

function ScoreboardConfigForm({ court }: { court: Court }) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState({
    scoreboardSocketUrl: court.scoreboardSocketUrl ?? "",
    scoreboardIdNodo: court.scoreboardIdNodo?.toString() ?? "",
  });

  const save = useMutation({
    mutationFn: () =>
      api.put(`/courts/${court.id}`, {
        scoreboardSocketUrl: values.scoreboardSocketUrl || null,
        scoreboardIdNodo: values.scoreboardIdNodo ? Number(values.scoreboardIdNodo) : null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["courts", court.clubId] });
      toast.success("Marcador configurado");
    },
    onError: () => toast.error("No se pudo guardar el marcador"),
  });

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-ink-100 bg-white p-4">
      <p className="flex items-center gap-2 text-sm font-medium text-ink-600">
        <Radio className="h-4 w-4 text-brand-600" />
        Marcador en vivo (server_puntopadel)
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
        <Input
          id={`scoreboard-url-${court.id}`}
          label="URL del socket"
          placeholder="http://localhost:49160"
          value={values.scoreboardSocketUrl}
          onChange={(e) => setValues((v) => ({ ...v, scoreboardSocketUrl: e.target.value }))}
        />
        <Input
          id={`scoreboard-nodo-${court.id}`}
          label="ID de nodo"
          placeholder="1"
          value={values.scoreboardIdNodo}
          onChange={(e) => setValues((v) => ({ ...v, scoreboardIdNodo: e.target.value }))}
        />
      </div>
      <div className="flex justify-end">
        <Button type="button" onClick={() => save.mutate()} disabled={save.isPending}>
          Guardar marcador
        </Button>
      </div>
    </div>
  );
}

function CameraManager({ court }: { court: Court }) {
  const queryClient = useQueryClient();
  const [editingCamera, setEditingCamera] = useState<Camera | null>(null);
  const [deletingCamera, setDeletingCamera] = useState<Camera | null>(null);
  const [previewCamera, setPreviewCamera] = useState<Camera | null>(null);

  const { data: cameras, isLoading } = useQuery({
    queryKey: ["cameras", court.id],
    queryFn: async () => (await api.get<Camera[]>(`/cameras?courtId=${court.id}`)).data,
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CameraForm>({ resolver: zodResolver(cameraSchema), defaultValues: { sourceMode: "PUSH" } });

  const sourceMode = watch("sourceMode");
  const showRtspField = sourceMode === "PULL";

  function startCreate() {
    setEditingCamera(null);
    reset({ name: "", sourceMode: "PUSH", rtspUrl: "" });
  }

  function startEdit(camera: Camera) {
    setEditingCamera(camera);
    reset({ name: camera.name, sourceMode: camera.sourceMode, rtspUrl: camera.rtspUrl ?? "" });
  }

  const saveCamera = useMutation({
    mutationFn: async (data: CameraForm): Promise<unknown> => {
      const payload = { name: data.name, sourceMode: data.sourceMode, rtspUrl: data.sourceMode === "PULL" ? data.rtspUrl : null };
      if (editingCamera) return api.put(`/cameras/${editingCamera.id}`, payload);
      return api.post("/cameras", { ...payload, courtId: court.id });
    },
    onSuccess: ({ data }: any) => {
      queryClient.invalidateQueries({ queryKey: ["cameras", court.id] });
      if (data?.warning) {
        toast.warning(data.warning);
      } else {
        toast.success(editingCamera ? "Cámara actualizada" : "Cámara añadida");
      }
      setEditingCamera(null);
      reset({ name: "", sourceMode: "PUSH", rtspUrl: "" });
    },
    onError: () => toast.error("No se pudo guardar la cámara"),
  });

  const deleteCamera = useMutation({
    mutationFn: (id: number) => api.delete(`/cameras/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cameras", court.id] });
      toast.success("Cámara eliminada");
    },
    onError: () => toast.error("No se pudo eliminar la cámara"),
  });

  return (
    <div className="flex flex-col gap-3">
      {isLoading ? (
        <p className="text-sm text-ink-400">Cargando cámaras…</p>
      ) : !cameras?.length ? (
        <p className="text-sm text-ink-400">Todavía no hay cámaras en esta pista.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {cameras.map((camera) => (
            <li key={camera.id} className="rounded-xl border border-ink-100 bg-white p-3">
              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={() => setPreviewCamera(previewCamera?.id === camera.id ? null : camera)}
                  className="flex min-w-0 flex-1 items-center gap-2 text-left"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                    <CameraIcon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <p className="flex items-center gap-1.5 truncate font-medium text-ink-800">
                      {camera.name}
                      {camera.sourceMode === "PUSH" && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-600">
                          <Router className="h-3 w-3" />
                          Empujada por túnel
                        </span>
                      )}
                    </p>
                    <p className="truncate text-xs text-ink-400">{camera.rtspUrl ?? "Esperando a que se conecte el equipo remoto…"}</p>
                  </span>
                </button>
                <div className="flex shrink-0 gap-1">
                  <Button variant="ghost" onClick={() => startEdit(camera)} title="Editar">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" onClick={() => setDeletingCamera(camera)} title="Eliminar">
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </Button>
                </div>
              </div>
              {camera.sourceMode === "PUSH" && camera.pushUrl && (
                <div className="mt-3 flex flex-col gap-1">
                  <p className="text-xs text-ink-400">URL a la que el equipo remoto de esta pista debe empujar el vídeo:</p>
                  <CopyableField value={camera.pushUrl} />
                </div>
              )}
              {previewCamera?.id === camera.id && (
                <LiveVideoPlayer src={camera.hlsUrl} className="mt-3 aspect-video w-full rounded-lg" />
              )}
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleSubmit((data) => saveCamera.mutate(data))} className="flex flex-col gap-3 rounded-xl bg-ink-50/60 p-4">
        <p className="text-sm font-medium text-ink-600">{editingCamera ? "Editar cámara" : "Nueva cámara"}</p>
        <Input id={`camera-name-${court.id}`} label="Nombre" error={errors.name?.message} {...register("name")} />

        <input type="hidden" {...register("sourceMode")} />

        {showRtspField ? (
          <div className="flex flex-col gap-2">
            <Input
              id={`camera-rtsp-${court.id}`}
              label="URL RTSP"
              placeholder="rtsp://usuario:contraseña@ip:554/..."
              error={errors.rtspUrl?.message}
              {...register("rtspUrl")}
            />
            <button
              type="button"
              onClick={() => setValue("sourceMode", "PUSH", { shouldValidate: true })}
              className="self-start text-xs font-medium text-violet-600 underline-offset-2 hover:underline"
            >
              Esta cámara ya no está en la misma red — pasarla a modo túnel
            </button>
          </div>
        ) : (
          <p className="rounded-lg bg-violet-50 px-3 py-2 text-xs text-violet-700">
            La cámara la empuja un equipo remoto en la pista (p.ej. una Raspberry Pi por túnel WireGuard). Al
            guardar, aquí abajo (y en la ficha de la cámara una vez creada) verás la URL a la que hay que
            configurarlo para que empuje el vídeo.
          </p>
        )}

        <div className="flex justify-end gap-2">
          {editingCamera && (
            <Button type="button" onClick={startCreate}>
              Cancelar edición
            </Button>
          )}
          <Button type="submit" disabled={saveCamera.isPending}>
            <Plus className="h-4 w-4" />
            {editingCamera ? "Guardar cambios" : "Añadir cámara"}
          </Button>
        </div>
      </form>

      {deletingCamera && (
        <ConfirmDialog
          open={!!deletingCamera}
          onOpenChange={(open) => !open && setDeletingCamera(null)}
          title="Eliminar cámara"
          description={`¿Seguro que quieres eliminar "${deletingCamera.name}"? Dejará de retransmitirse.`}
          tone="danger"
          confirmLabel="Eliminar"
          onConfirm={() => deleteCamera.mutate(deletingCamera.id)}
        />
      )}
    </div>
  );
}

export function CourtDetailPage() {
  const { courtId } = useParams();
  const courtIdNum = Number(courtId);

  const { data: court, isLoading } = useQuery({
    queryKey: ["court", courtIdNum],
    queryFn: async () => (await api.get<CourtWithClub>(`/courts/${courtIdNum}`)).data,
  });

  if (isLoading || !court) {
    return <p className="text-sm text-ink-400">Cargando pista…</p>;
  }

  return (
    <div className="flex flex-col gap-8">
      <Breadcrumbs
        items={[
          { label: "Clubes", to: "/clubes" },
          { label: court.club.nombre, to: `/clubes/${court.club.id}` },
          { label: court.name },
        ]}
      />

      <div className="flex items-center gap-4">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
          <MapPin className="h-6 w-6" />
        </span>
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-ink-900">
            {court.name}
            <Badge tone={court.active ? "green" : "red"}>{court.active ? "Activa" : "Inactiva"}</Badge>
          </h1>
          <p className="text-ink-400">{court.club.nombre}</p>
        </div>
      </div>

      <Card className="flex flex-col gap-5">
        <CourtInfoForm court={court} />
        <ScoreboardConfigForm court={court} />
        <DeviceListEditor
          clubId={court.clubId}
          courtId={court.id}
          type="SCREEN_SCOREBOARD"
          title="Pantalla del marcador"
          icon={Monitor}
          emptyLabel="Esta pista todavía no tiene pantalla de marcador."
        />
        <CameraManager court={court} />
      </Card>
    </div>
  );
}
